import { useState } from "react";
import { useSearchParams, Link } from "react-router";
import {
  addDays,
  focusBlocks,
  isIsoDate,
  isScheduledOn,
  MAX_PREP_DAYS,
  minutesToTime,
  needsPrepOn,
  timeToMinutes,
  type CheckInStatus,
  type HabitDto,
  type TaskDto,
} from "@habitos/shared";
import { useAuth } from "../lib/auth";
import { useCheckIns, useGoals, useHabits, useMoveTasks, useSetCheckIn, useSetTaskDone, useTasks } from "../lib/queries";
import { formatDay, formatDuration, relativeDayLabel } from "../lib/format";
import { PushBanner } from "../components/PushBanner";
import { FocusTimer } from "../components/FocusTimer";

/** How far "Lo que viene" looks ahead. */
const AHEAD_DAYS = 7;

type Entry = { kind: "habit"; time: string; habit: HabitDto } | { kind: "task"; time: string; task: TaskDto };

export function Today() {
  const { user, today } = useAuth();
  const [params, setParams] = useSearchParams();
  const requested = params.get("date");
  const date = requested && isIsoDate(requested) ? requested : today;

  const habits = useHabits();
  const checkIns = useCheckIns(date, date);
  const setCheckIn = useSetCheckIn();
  // Far enough ahead to catch every task whose prep window has already started.
  const tasks = useTasks(date, addDays(date, Math.max(AHEAD_DAYS, MAX_PREP_DAYS)));
  const setTaskDone = useSetTaskDone();
  const moveTasks = useMoveTasks();
  const goals = useGoals();
  const struggling = (goals.data ?? []).find((g) => g.progress.needsReplan);
  const [focusing, setFocusing] = useState<TaskDto | null>(null);

  const todaysHabits = (habits.data ?? []).filter((h) => isScheduledOn(h, date));
  const todaysTasks = (tasks.data ?? []).filter((t) => t.date === date);
  const untimed = todaysTasks.filter((t) => t.time === null);
  const timeline: Entry[] = [
    ...todaysHabits.map((habit) => ({ kind: "habit" as const, time: habit.time, habit })),
    ...todaysTasks.flatMap((task) => (task.time === null ? [] : [{ kind: "task" as const, time: task.time, task }])),
  ].sort((a, b) => a.time.localeCompare(b.time));
  const ahead = (tasks.data ?? []).filter((t) => t.date > date && t.date <= addDays(date, AHEAD_DAYS));
  const toPrepare = (tasks.data ?? []).filter((t) => needsPrepOn(t, date));

  const statusOf = (habitId: string) => checkIns.data?.find((c) => c.habitId === habitId)?.status;
  const total = todaysHabits.length + todaysTasks.length;
  const done =
    todaysHabits.filter((h) => statusOf(h.id) === "done").length + todaysTasks.filter((t) => t.done).length;

  const go = (d: string) => setParams(d === today ? {} : { date: d });
  const toggle = (habitId: string, status: CheckInStatus) =>
    setCheckIn.mutate({ habitId, date, status: statusOf(habitId) === status ? null : status });
  const toggleTask = (task: TaskDto) => setTaskDone.mutate({ id: task.id, done: !task.done });

  // What wasn't done goes to the next day; from a day already gone, it comes to today.
  const moveTarget = date < today ? today : addDays(date, 1);
  const moveTo = date < today ? "a hoy" : moveTarget === addDays(today, 1) ? "a mañana" : `al ${formatDay(moveTarget)}`;
  const pendingTasks = todaysTasks.filter((t) => !t.done);
  const moveTask = (task: TaskDto) => moveTasks.mutate({ from: date, to: moveTarget, ids: [task.id] });

  const loading = habits.isPending || tasks.isPending;

  return (
    <>
      <header className="page-header">
        <p className="muted">{date === today ? `Hola, ${user.name}` : formatDay(date)}</p>
        <div className="day-nav">
          <button className="icon-btn" aria-label="Día anterior" onClick={() => go(addDays(date, -1))}>‹</button>
          <h1>{relativeDayLabel(date, today)}</h1>
          <button className="icon-btn" aria-label="Día siguiente" onClick={() => go(addDays(date, 1))}>›</button>
        </div>
        {total > 0 && (
          <div className="progress" role="progressbar" aria-valuenow={done} aria-valuemax={total}>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${(done / total) * 100}%` }} />
            </div>
            <span>{done} de {total}</span>
          </div>
        )}
      </header>

      <PushBanner />
      {struggling && date === today && (
        <Link to={`/metas/${struggling.id}`} className="banner">
          “{struggling.title}” se ha complicado esta semana. ¿Ajustamos el plan para que sea más fácil?
        </Link>
      )}

      {toPrepare.length > 0 && (
        <>
          <h2 className="section">Para preparar</h2>
          <ul className="list">
            {toPrepare.map((task) => (
              <li key={task.id}>
                <Link to={`/tareas/${task.id}`} className="prep card">
                  <span className="prep-icon" aria-hidden>🔔</span>
                  <span className="grow">
                    <span className="habit-title">{task.prepNote ?? task.title}</span>
                    <span className="muted small prep-for">
                      Para “{task.emoji ? `${task.emoji} ` : ""}{task.title}”, {whenLabel(task.date, today)}
                      {task.time ? ` a las ${task.time}` : ""}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {loading ? (
        <p className="muted center">Cargando…</p>
      ) : total === 0 ? (
        <div className="empty card">
          <p>{(habits.data ?? []).length === 0 ? "Aún no tienes nada en tu día." : "Nada programado para este día."}</p>
          <Link to={`/tareas/nueva?date=${date}`} className="btn primary">Agregar una tarea</Link>
          <Link to="/asistente" className="btn">Armar mi horario con el asistente</Link>
          <Link to="/habitos/nuevo" className="btn">Crear un hábito</Link>
        </div>
      ) : (
        <>
          {untimed.length > 0 && (
            <>
              <h2 className="section">Durante el día</h2>
              <ul className="list">
                {untimed.map((task) => (
                  <TaskItem key={task.id} task={task} onToggle={toggleTask} onFocus={setFocusing} onMove={moveTask} moveTo={moveTo} />
                ))}
              </ul>
            </>
          )}
          {timeline.length > 0 && (
            <>
              {untimed.length > 0 && <h2 className="section">Por hora</h2>}
              <ul className="list">
                {timeline.map((entry) =>
                  entry.kind === "habit" ? (
                    <HabitItem key={`h-${entry.habit.id}`} habit={entry.habit} status={statusOf(entry.habit.id)} onToggle={toggle} />
                  ) : (
                    <TaskItem
                      key={`t-${entry.task.id}`}
                      task={entry.task}
                      onToggle={toggleTask}
                      onFocus={setFocusing}
                      onMove={moveTask}
                      moveTo={moveTo}
                    />
                  ),
                )}
              </ul>
            </>
          )}
        </>
      )}

      {!loading && date <= today && pendingTasks.length > 0 && (
        <button
          className="btn move-pending"
          disabled={moveTasks.isPending}
          onClick={() => moveTasks.mutate({ from: date, to: moveTarget })}
        >
          Pasar {pendingTasks.length === 1 ? "la tarea pendiente" : `las ${pendingTasks.length} tareas pendientes`} {moveTo}
        </button>
      )}

      {!loading && total > 0 && (
        <Link to={`/tareas/nueva?date=${date}`} className="btn add-task">+ Agregar tarea</Link>
      )}

      {ahead.length > 0 && (
        <>
          <h2 className="section">Lo que viene</h2>
          <ul className="list">
            {ahead.map((task) => (
              <li key={task.id}>
                <button className="ahead card" onClick={() => go(task.date)} style={{ borderLeftColor: task.color }}>
                  <span className="ahead-day">{relativeDayLabel(task.date, today)}</span>
                  <span className="grow">{task.emoji} {task.title}</span>
                  {task.time && <span className="muted small">{task.time}</span>}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {focusing && (
        <FocusTimer
          task={focusing}
          onClose={() => setFocusing(null)}
          onFinish={() => {
            if (!focusing.done) toggleTask(focusing);
            setFocusing(null);
          }}
        />
      )}
    </>
  );
}

/** "mañana", "hoy", or "el vie 9 oct", to finish a sentence. */
function whenLabel(date: string, today: string): string {
  const label = relativeDayLabel(date, today);
  return label === formatDay(date) ? `el ${label}` : label.toLowerCase();
}

function HabitItem({
  habit,
  status,
  onToggle,
}: {
  habit: HabitDto;
  status: CheckInStatus | undefined;
  onToggle: (habitId: string, status: CheckInStatus) => void;
}) {
  return (
    <li className={`habit card ${status ?? ""}`} style={{ borderLeftColor: habit.color }}>
      <div className="habit-time">{habit.time}</div>
      <div className="habit-body">
        <div className="habit-title">{habit.emoji} {habit.title}</div>
        <div className="muted small">{formatDuration(habit.durationMinutes)} · hábito</div>
      </div>
      <div className="habit-actions">
        <button
          className={`check ${status === "done" ? "on" : ""}`}
          aria-label="Hecho"
          aria-pressed={status === "done"}
          onClick={() => onToggle(habit.id, "done")}
        >✓</button>
        <button
          className={`skip ${status === "skipped" ? "on" : ""}`}
          aria-label="Hoy no"
          aria-pressed={status === "skipped"}
          onClick={() => onToggle(habit.id, "skipped")}
        >✕</button>
      </div>
    </li>
  );
}

function TaskItem({
  task,
  onToggle,
  onFocus,
  onMove,
  moveTo,
}: {
  task: TaskDto;
  onToggle: (task: TaskDto) => void;
  onFocus: (task: TaskDto) => void;
  onMove: (task: TaskDto) => void;
  /** "a mañana", "a hoy" or "al jue 8 oct", for the move button's label. */
  moveTo: string;
}) {
  const blocks = task.focus ? focusBlocks(task) : [];
  const start = task.time ? timeToMinutes(task.time) : null;
  return (
    <li className={`habit task card ${task.done ? "done" : ""}`} style={{ borderLeftColor: task.color }}>
      <div className="task-main">
        <div className="habit-time">{task.time ?? ""}</div>
        <Link to={`/tareas/${task.id}`} className="habit-body">
          <div className="habit-title">{task.emoji} {task.title}</div>
          <div className="muted small">
            {formatDuration(task.durationMinutes)}
            {task.focus && ` · ${blocks.filter((b) => b.kind === "work").length} bloques`}
          </div>
          {task.notes && <div className="muted small task-notes">{task.notes}</div>}
        </Link>
        <div className="habit-actions">
          {!task.done && (
            <button className="move" aria-label={`Pasar ${moveTo}`} title={`Pasar ${moveTo}`} onClick={() => onMove(task)}>
              →
            </button>
          )}
          <button
            className={`check ${task.done ? "on" : ""}`}
            aria-label="Hecha"
            aria-pressed={task.done}
            onClick={() => onToggle(task)}
          >✓</button>
        </div>
      </div>
      {task.focus && !task.done && (
        <div className="blocks">
          <ol className="block-list">
            {blocks.map((b) => (
              <li key={b.offset} className={`block ${b.kind}`}>
                {start !== null && <span className="block-time">{minutesToTime(start + b.offset)}</span>}
                {b.kind === "work" ? "Trabajo" : "Pausa: estírate"} · {b.minutes} min
              </li>
            ))}
          </ol>
          <button className="btn primary" onClick={() => onFocus(task)}>▶ Empezar</button>
        </div>
      )}
    </li>
  );
}
